"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHistoricalTVL = getHistoricalTVL;
exports.getLatestTVL = getLatestTVL;
exports.getDailyRevenue = getDailyRevenue;
exports.getDailyStSuiRevenues = getDailyStSuiRevenues;
exports.getStSuiMaxDailyRevenue = getStSuiMaxDailyRevenue;
exports.getAnnualisedRevenue = getAnnualisedRevenue;
exports.getTotalAlphaStakers = getTotalAlphaStakers;
const alphafi_sdk_1 = require("@alphafi/alphafi-sdk");
const decimal_js_1 = require("decimal.js");
const index_js_1 = require("../protocol/index.js");
const index_js_2 = require("./index.js");
const stsui_sdk_1 = require("@alphafi/stsui-sdk");
const dailyRevenue_js_1 = require("./crons/dailyRevenue.js");
const TVL_js_1 = require("./crons/TVL.js");
const dailyRevenue_js_2 = require("../alphalend/dailyRevenue.js");
const lastDayMarketUpdateEvents_js_1 = require("../alphalend/lastDayMarketUpdateEvents.js");
const logger_js_1 = require("../logger.js");
// Local testing imports
// --------------------------------
const dailyRevenue_js_3 = require("./crons/dailyRevenue.js");
const dynamoDBClient_js_1 = require("../dynamoDBClient.js");
const constants_js_1 = require("../pyth/constants.js");
// --------------------------------
// fetch this from alphafi-sdk constants
// const ALPHAFI_INCEPTION_TIME = 1719501255854;
async function getHistoricalTVL(params) {
    let maxTime = Number.MIN_VALUE, minTime = Number.MAX_VALUE;
    const coinPairs = new Set();
    params.tvlTokens.forEach((tvlTokensObject) => {
        minTime = Math.min(minTime, tvlTokensObject.time);
        maxTime = Math.max(maxTime, tvlTokensObject.time);
        coinPairs.add(`${tvlTokensObject.coin}/USD`);
    });
    // we need prices for alphalend coins, but these coins don't appear in tvl tokens.
    // but we add those coins to coinGeckoPriceFeed so we get the coinPairs from there
    Object.keys(constants_js_1.coinGeckoPriceFeed).forEach((coinPair) => {
        coinPairs.add(coinPair);
    });
    const historicalPrices = await (0, index_js_2.fetchHistoricalPrices)({
        minTime,
        maxTime,
        coinPairs: Array.from(coinPairs),
    });
    const alphaFiTVL = calculateTvlFromTokens({
        tvlTokens: params.tvlTokens,
        historicalPrices: historicalPrices,
    })
        .sort((a, b) => Number(a.time) - Number(b.time))
        .filter((o) => o.TVL !== "0");
    const stSuiTVL = await getHistoricalStSuiTVL();
    const alphalendTVL = await getAlphalendHistoricalTVL(historicalPrices);
    const mergedMap = new Map();
    const addToMap = (array) => {
        for (const { time, TVL } of array) {
            const currentValue = mergedMap.get(time) || new decimal_js_1.Decimal(0);
            mergedMap.set(time, currentValue.add(new decimal_js_1.Decimal(TVL)));
        }
    };
    addToMap(alphaFiTVL);
    addToMap(stSuiTVL);
    addToMap(alphalendTVL);
    return Array.from(mergedMap.entries())
        .map(([time, TVL]) => ({
        time,
        TVL: TVL.toString(),
    }))
        .sort((a, b) => Number(a.time) - Number(b.time))
        .slice(-120); // only showing 120 days graph
}
async function getHistoricalStSuiTVL() {
    const tvl = [];
    const epochChangeEvents = (await stsui_sdk_1.Events.getEpochChangeEvents({
        endTime: (0, dailyRevenue_js_1.getEndOfDayGMT)(Date.now() - 24 * 60 * 60 * 1000),
        typeName: (0, stsui_sdk_1.getConf)().STSUI_COIN_TYPE,
    })).sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
    const maxTime = (0, dailyRevenue_js_1.getEndOfDayGMT)(Number(epochChangeEvents[epochChangeEvents.length - 1].timestamp));
    const minTime = (0, TVL_js_1.getStartOfDayGMT)(Number(epochChangeEvents[0].timestamp));
    const historicalStSuiPrices = (await (0, index_js_2.fetchHistoricalPrices)({ maxTime, minTime, coinPairs: ["STSUI/USD"] }))["STSUI/USD"];
    epochChangeEvents.forEach((e) => {
        const time = (0, dailyRevenue_js_1.getEndOfDayGMT)(Number(e.timestamp));
        const stSuiPricePair = historicalStSuiPrices.find(([t]) => Number(t) === Number(time));
        if (!stSuiPricePair) {
            (0, logger_js_1.logError)("StSui price not found for timestamp, skipping entry", {
                component: "protocol",
                operation: "getHistoricalStSuiTVL",
                context: { timestamp: time },
            });
            return;
        }
        const TVL = new decimal_js_1.Decimal(e.event.lst_supply)
            .minus(new decimal_js_1.Decimal(e.event.spread_fee))
            .div(new decimal_js_1.Decimal(1e9))
            .mul(new decimal_js_1.Decimal(stSuiPricePair[1]));
        tvl.push({ time: time.toString(), TVL: TVL.toString() });
    });
    return tvl;
}
async function getAlphalendHistoricalTVL(historicalPrices) {
    // console.time("getAlphalendHistoricalTVL"); //debug
    let minTime = Number.MAX_VALUE;
    let maxTime = Number.MIN_VALUE;
    for (const coin in historicalPrices) {
        for (const timePricePair of historicalPrices[coin]) {
            minTime = Math.min(minTime, timePricePair[0]);
            maxTime = Math.max(maxTime, timePricePair[0]);
        }
    }
    const marketUpdateEvents = await (0, lastDayMarketUpdateEvents_js_1.fetchAlphalendMarketUpdateEventsSentio)(minTime, maxTime);
    if (marketUpdateEvents.length === 0) {
        return []; // No events, no TVL to calculate
    }
    // accumulate the relevant events for each day
    const lastEventEachMarketsPerDay = {};
    for (const event of marketUpdateEvents) {
        const day = (0, dailyRevenue_js_1.getEndOfDayGMT)(event.timestamp);
        if (!lastEventEachMarketsPerDay[day]) {
            lastEventEachMarketsPerDay[day] = [];
        }
        lastEventEachMarketsPerDay[day].push(event);
    }
    // handle if there is no event for some market some day
    for (let i = 1; i < Object.keys(lastEventEachMarketsPerDay).length; i++) {
        const currentDay = Number(Object.keys(lastEventEachMarketsPerDay)[i]);
        const previousDay = Number(Object.keys(lastEventEachMarketsPerDay)[i - 1]);
        const previousDayEvents = lastEventEachMarketsPerDay[previousDay];
        const currentDayEvents = lastEventEachMarketsPerDay[currentDay];
        // Create a map of market IDs in the current day for quick lookup
        const currentDayMarketIds = new Set(currentDayEvents.map((event) => event.market));
        // Check if any markets from the previous day are missing in the current day
        for (const prevEvent of previousDayEvents) {
            const marketId = prevEvent.market;
            // Only consider events with non-zero balance_holding
            // do not carry formward 0 balance_holding
            if (new decimal_js_1.Decimal(prevEvent.balance_holding).isZero()) {
                continue;
            }
            if (!currentDayMarketIds.has(marketId)) {
                (0, logger_js_1.logInfo)("Market missing for day, copying from previous day", {
                    component: "protocol",
                    operation: "getAlphalendHistoricalTVL",
                    context: {
                        marketId,
                        missingDay: new Date(currentDay).toISOString().split("T")[0],
                    },
                });
                // Copy the event from the previous day, but increment the timestamp and date
                const copiedEvent = {
                    ...prevEvent,
                    timestamp: prevEvent.timestamp + 24 * 60 * 60 * 1000,
                    date: new Date(new Date(prevEvent.date).getTime() + 24 * 60 * 60 * 1000).toString(),
                };
                // Add the copied event to the current day
                lastEventEachMarketsPerDay[currentDay].push(copiedEvent);
            }
        }
    }
    // price map for easy access
    const historicalPricesMap = {};
    Object.entries(historicalPrices).forEach(([coinPair, prices]) => {
        historicalPricesMap[coinPair] = Object.fromEntries(prices);
    });
    // calculate the tvl
    const tvl = [];
    for (const [day, allEvents] of Object.entries(lastEventEachMarketsPerDay)) {
        let thisDayTvl = new decimal_js_1.Decimal(0);
        for (const event of allEvents) {
            const coin_type = event.coin_type;
            const coin = Object.entries(alphafi_sdk_1.coinsList).find((o) => o[1].type === coin_type);
            if (!coin) {
                throw new Error(`Coin not found for ${coin_type}`);
            }
            const coinPrice = historicalPricesMap[`${coin[0]}/USD`][Number(day)];
            const thisMarketTvl = new decimal_js_1.Decimal(event.balance_holding)
                .div(new decimal_js_1.Decimal(10).pow(coin[1].expo))
                .mul(new decimal_js_1.Decimal(coinPrice));
            thisDayTvl = thisDayTvl.add(thisMarketTvl);
        }
        tvl.push({
            time: day,
            TVL: thisDayTvl.toString(),
        });
    }
    return tvl;
}
async function getLatestTVL() {
    let response = "";
    response = (await (0, index_js_1.fetchAllAppsTVL)(false)).total;
    return response;
}
// export async function getDailyRevenue(): Promise<string> {
//   const dailyRevenueMap: { [time: number]: Decimal } = {};
//   const revenue_url = "https://api.alphafi.xyz/dailyRevenue/usd";
//   const revenueResponse = await fetch(revenue_url);
//   if (!revenueResponse.ok) {
//     const errorText = await revenueResponse.text();
//     throw new Error(
//       `HTTP error! status: ${revenueResponse.status}, response: ${errorText}`,
//     );
//   }
//   const revenueItems: {
//     usdAmount: string;
//     time: number;
//     poolName: PoolName;
//   }[] = await revenueResponse.json();
//   revenueItems.forEach((item) => {
//     const time = getEndOfDayGMT(item.time);
//     if (!dailyRevenueMap[time]) dailyRevenueMap[time] = new Decimal(0);
//     dailyRevenueMap[time] = dailyRevenueMap[time].add(
//       new Decimal(item.usdAmount),
//     );
//   });
//   const stSuiRevenues = await getDailyStSuiRevenues(1);
//   for (const [time, revenue] of Object.entries(stSuiRevenues)) {
//     const numericTime = Number(time);
//     if (!dailyRevenueMap[numericTime])
//       dailyRevenueMap[numericTime] = new Decimal(0);
//     dailyRevenueMap[numericTime] = dailyRevenueMap[numericTime].add(
//       new Decimal(revenue),
//     );
//   }
//   // special case patch deleting entry for Jan 3rd 2025
//   if (dailyRevenueMap[1736035199999]) {
//     delete dailyRevenueMap[1736035199999];
//   }
//   const alphalendRevenue = await getDailyAlphalendRevenue(1);
//   for (const [time, revenue] of Object.entries(alphalendRevenue)) {
//     const numericTime = Number(time);
//     if (!dailyRevenueMap[numericTime])
//       dailyRevenueMap[numericTime] = new Decimal(0);
//     dailyRevenueMap[numericTime] = dailyRevenueMap[numericTime].add(
//       new Decimal(revenue),
//     );
//   }
//   const [, maxRevenue] = Object.entries(dailyRevenueMap).reduce<
//     [number, Decimal]
//   >(
//     (best, [t, rev]) => {
//       const time = Number(t);
//       // if this entry's time is bigger, replace best; otherwise keep old
//       return time > best[0] ? [time, rev] : best;
//     },
//     [Number.NEGATIVE_INFINITY, new Decimal(0)],
//   );
//   return maxRevenue.toString();
// }
// Local version of getDailyRevenue, substitue for local testing
// --------------------------------
async function getDailyRevenue() {
    const dailyRevenueMap = {};
    const yesterdayEnd = (0, dailyRevenue_js_1.getEndOfDayGMT)(Date.now() - 24 * 60 * 60 * 1000);
    const revenue = await (0, dailyRevenue_js_3.getDailyRevenueDataFromDynamoDB)({
        tableName: "DailyRevenueTable",
        ddbClient: (0, dynamoDBClient_js_1.getDynamoDBClient)(),
    });
    const revenueItems = [];
    revenue.forEach((item) => {
        if (item.usdAmount.S && item.time.N && item.poolName.S) {
            const time = Number(item.time.N);
            const usdAmount = item.usdAmount.S;
            const poolName = item.poolName.S;
            revenueItems.push({
                usdAmount,
                time,
                poolName,
            });
            const endTime = (0, dailyRevenue_js_1.getEndOfDayGMT)(time);
            if (endTime !== yesterdayEnd) {
                return;
            }
            if (!dailyRevenueMap[endTime])
                dailyRevenueMap[endTime] = {
                    Alphafi: new decimal_js_1.Decimal(0),
                    Alphalend: new decimal_js_1.Decimal(0),
                    Stsui: new decimal_js_1.Decimal(0),
                };
            dailyRevenueMap[endTime].Alphafi = dailyRevenueMap[endTime].Alphafi.add(new decimal_js_1.Decimal(usdAmount));
        }
        else {
            (0, logger_js_1.logError)("Improper entry in the DB, missing required properties", {
                component: "protocol",
                operation: "getDailyRevenue",
                context: { item },
            });
        }
    });
    const stSuiRevenues = await getDailyStSuiRevenues(1);
    for (const [time, revenue] of Object.entries(stSuiRevenues)) {
        const numericTime = Number(time);
        if (!dailyRevenueMap[numericTime])
            dailyRevenueMap[numericTime] = {
                Alphafi: new decimal_js_1.Decimal(0),
                Alphalend: new decimal_js_1.Decimal(0),
                Stsui: new decimal_js_1.Decimal(0),
            };
        dailyRevenueMap[numericTime].Stsui = dailyRevenueMap[numericTime].Stsui.add(new decimal_js_1.Decimal(revenue));
    }
    const alphalendRevenue = await getDailyAlphalendRevenue(1);
    for (const [time, revenue] of Object.entries(alphalendRevenue)) {
        const numericTime = Number(time);
        if (!dailyRevenueMap[numericTime])
            dailyRevenueMap[numericTime] = {
                Alphafi: new decimal_js_1.Decimal(0),
                Alphalend: new decimal_js_1.Decimal(0),
                Stsui: new decimal_js_1.Decimal(0),
            };
        dailyRevenueMap[numericTime].Alphalend = dailyRevenueMap[numericTime].Alphalend.add(new decimal_js_1.Decimal(revenue));
    }
    // const [, maxRevenue] = Object.entries(dailyRevenueMap).reduce<
    //   [number, Decimal]
    // >(
    //   (best, [t, rev]) => {
    //     const time = Number(t);
    //     // if this entry's time is bigger, replace best; otherwise keep old
    //     return time > best[0] ? [time, rev] : best;
    //   },
    //   [Number.NEGATIVE_INFINITY, new Decimal(0)],
    // );
    return {
        Alphafi: dailyRevenueMap[yesterdayEnd].Alphafi.toString(),
        Alphalend: dailyRevenueMap[yesterdayEnd].Alphalend.toString(),
        Stsui: dailyRevenueMap[yesterdayEnd].Stsui.toString(),
        total: dailyRevenueMap[yesterdayEnd].Alphafi.add(dailyRevenueMap[yesterdayEnd].Alphalend)
            .add(dailyRevenueMap[yesterdayEnd].Stsui)
            .toString(),
    };
}
// --------------------------------
async function getDailyStSuiRevenues(days) {
    const startTime = (0, TVL_js_1.getStartOfDayGMT)(Date.now() - days * 24 * 60 * 60 * 1000);
    const endTime = (0, dailyRevenue_js_1.getEndOfDayGMT)(Date.now() - 24 * 60 * 60 * 1000);
    // No fees on mint, reedem etc. yet, will need to add them if fee is introduced
    const [epochChangeEvents, 
    // mintEvents,
    // redeemEvents,
    // flashStakeEvents,
    suiHistoricalPrices,] = await Promise.all([
        stsui_sdk_1.Events.getEpochChangeEvents({
            startTime,
            endTime,
            typeName: (0, stsui_sdk_1.getConf)().STSUI_COIN_TYPE,
        }),
        // Events.getMintEvents({
        //   startTime,
        //   endTime,
        //   typeName: getStSuiConf().STSUI_COIN_TYPE,
        // }),
        // Events.getRedeemEvents({
        //   startTime,
        //   endTime,
        //   typeName: getStSuiConf().STSUI_COIN_TYPE,
        // }),
        // Events.getFlashStakeEvents({
        //   startTime,
        //   endTime,
        //   typeName: getStSuiConf().STSUI_COIN_TYPE,
        // }),
        (0, index_js_2.fetchHistoricalPrices)({
            maxTime: endTime,
            minTime: startTime,
            coinPairs: ["SUI/USD"],
        }),
    ]);
    const dailyRevenueMap = {};
    for (const event of epochChangeEvents) {
        const time = (0, dailyRevenue_js_1.getEndOfDayGMT)(Number(event.timestamp));
        const timePricePair = suiHistoricalPrices["SUI/USD"].find((o) => o[0] === time);
        if (!timePricePair) {
            (0, logger_js_1.logError)("Sui price not found for StSui revenue calculation, skipping entry", {
                component: "protocol",
                operation: "getDailyStSuiRevenues",
                context: { timestamp: event.timestamp },
            });
            continue;
        }
        const fees = new decimal_js_1.Decimal(event.event.spread_fee)
            .div(1e9)
            .mul(new decimal_js_1.Decimal(timePricePair[1]));
        if (!dailyRevenueMap[time])
            dailyRevenueMap[time] = new decimal_js_1.Decimal(0);
        dailyRevenueMap[time] = dailyRevenueMap[time].add(fees);
    }
    // for (const event of [...mintEvents, ...redeemEvents, ...flashStakeEvents]) {
    //   const time = getEndOfDayGMT(Number(event.timestamp));
    //   const timePricePair = suiHistoricalPrices["SUI/USD"].find(
    //     (o) => o[0] === time,
    //   );
    //   if (!timePricePair) {
    //     console.warn(
    //       `Sui price not found for ${event.timestamp}. Skipping this entry.`,
    //     );
    //     continue;
    //   }
    //   const fees = new Decimal(event.event.fee_amount)
    //     .div(1e9)
    //     .mul(new Decimal(timePricePair[1]));
    //   if (!dailyRevenueMap[time]) dailyRevenueMap[time] = new Decimal(0);
    //   dailyRevenueMap[time] = dailyRevenueMap[time].add(fees);
    // }
    return Object.fromEntries(Object.entries(dailyRevenueMap).map(([time, revenue]) => [
        time,
        revenue.toString(),
    ]));
}
async function getStSuiMaxDailyRevenue() {
    const dailyRevenues = await getDailyStSuiRevenues(7);
    const maxRevenue = Object.values(dailyRevenues).reduce((max, curr) => new decimal_js_1.Decimal(curr).greaterThan(max) ? new decimal_js_1.Decimal(curr) : max, new decimal_js_1.Decimal(0));
    return maxRevenue.toString();
}
async function calculateAlphalendRevenueUsd(alphalendMarketRevenueTokens) {
    let minTime = Number.MAX_SAFE_INTEGER;
    let maxTime = 0;
    const coinPairs = [];
    alphalendMarketRevenueTokens.forEach((revenueTokenObj) => {
        minTime = (0, TVL_js_1.getStartOfDayGMT)(Math.min(minTime, revenueTokenObj.time));
        maxTime = (0, dailyRevenue_js_1.getEndOfDayGMT)(Math.max(maxTime, revenueTokenObj.time));
        const coinName = Object.keys(alphafi_sdk_1.coinsList).find((coinName) => alphafi_sdk_1.coinsList[coinName].type === revenueTokenObj.marketCoinType);
        if (!coinName) {
            throw new Error(`Coin name not found for coin type: ${revenueTokenObj.marketCoinType}`);
        }
        coinPairs.push(`${coinName}/USD`);
    });
    const historicalPrices = await (0, index_js_2.fetchHistoricalPrices)({
        maxTime,
        minTime,
        coinPairs,
    });
    const alphalendMarketRevnueUsd = [];
    alphalendMarketRevenueTokens.map((revenueTokenObj) => {
        const coinName = Object.keys(alphafi_sdk_1.coinsList).find((coinName) => alphafi_sdk_1.coinsList[coinName].type === revenueTokenObj.marketCoinType);
        if (!coinName) {
            throw new Error(`Coin name not found for coin type: ${revenueTokenObj.marketCoinType}`);
        }
        const timePricePair = historicalPrices[`${coinName}/USD`].find((o) => o[0] === revenueTokenObj.time);
        if (!timePricePair) {
            throw new Error(`Price not found for ${revenueTokenObj.time}`);
        }
        alphalendMarketRevnueUsd.push({
            ...revenueTokenObj,
            coinName,
            usdAmount: new decimal_js_1.Decimal(revenueTokenObj.coinAmount)
                .mul(timePricePair[1])
                .toString(),
        });
    });
    const dailyRevenueMap = {};
    alphalendMarketRevnueUsd.forEach((revenueObj) => {
        if (!dailyRevenueMap[revenueObj.time]) {
            dailyRevenueMap[revenueObj.time] = new decimal_js_1.Decimal(0);
        }
        dailyRevenueMap[revenueObj.time] = dailyRevenueMap[revenueObj.time].add(revenueObj.usdAmount);
    });
    return Object.fromEntries(Object.entries(dailyRevenueMap).map(([time, revenue]) => [
        time,
        revenue.toString(),
    ]));
}
async function getDailyAlphalendRevenue(days) {
    const today = new Date();
    let alphalendRevenueTokens = [];
    for (let i = 0; i < days; i++) {
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() - 1 - i);
        const dateString = targetDate.toISOString().split("T")[0];
        // Fetch token revenues
        alphalendRevenueTokens = alphalendRevenueTokens.concat(await (0, dailyRevenue_js_2.fetchAlphalendRevenueTokensForDate)(dateString));
        await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    // Convert token revenues to USD
    const alphalendRevenueUsd = await calculateAlphalendRevenueUsd(alphalendRevenueTokens);
    return alphalendRevenueUsd;
}
async function getAnnualisedRevenue(params) {
    const { dailyRevenue } = params;
    const annualisedRevenue = new decimal_js_1.Decimal(dailyRevenue).mul(365);
    const response = annualisedRevenue.toString();
    return response;
}
async function getTotalAlphaStakers(params) {
    let response = 0;
    const { alphaHoldings } = params;
    response = Number(alphaHoldings.replace(/,/g, ""));
    return response;
}
function calculateTvlFromTokens(params) {
    const { historicalPrices, tvlTokens } = params;
    const tvl = [];
    tvlTokens.map((o) => {
        const timePricePair = historicalPrices[`${o.coin}/USD`].find((pair) => {
            return pair[0] === Number(o.time);
        });
        if (!timePricePair) {
            throw new Error(`price not found for ${o.coin} at ${o.time}`);
        }
        const usdTvl = new decimal_js_1.Decimal(timePricePair[1]).mul(new decimal_js_1.Decimal(o.amount));
        const thisDayTVL = tvl.find((entry) => Number(entry.time) === Number(o.time));
        if (thisDayTVL) {
            thisDayTVL.TVL = new decimal_js_1.Decimal(thisDayTVL.TVL).add(usdTvl).toString();
        }
        else {
            tvl.push({ time: o.time.toString(), TVL: usdTvl.toString() });
        }
    });
    return tvl;
}
/*
export async function getDailyRevenue(): Promise<string> {
  let response = new Decimal(0);

  const revenue_url = "https://api.alphafi.xyz/dailyRevenue/usd";
  const revenueResponse = await fetch(revenue_url);
  if (!revenueResponse.ok) {
    const errorText = await revenueResponse.text();
    throw new Error(
      `HTTP error! status: ${revenueResponse.status}, response: ${errorText}`,
    );
  }
  const revenueItems: {
    usdAmount: string;
    time: number;
    poolName: PoolName;
  }[] = await revenueResponse.json();
  revenueItems.forEach((item) => {
    response = response.add(new Decimal(item.usdAmount));
  });

  return response
    .div(new Set<number>(revenueItems.map((item) => item.time)).size)
    .add(new Decimal(await getStSuiAverageWeeklyRevenue()))
    .toString();
}

export async function getStSuiAverageWeeklyRevenue(): Promise<string> {
  let response = new Decimal(0);
  const startTime = getStartOfDayGMT(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const endTime = getEndOfDayGMT(Date.now() - 24 * 60 * 60 * 1000);
  const [
    epochChangeEvents,
    mintEvents,
    redeemEvents,
    flashStakeEvents,
    suiHistoricalPrices,
  ] = await Promise.all([
    Events.getEpochChangeEvents({ startTime, endTime }),
    Events.getMintEvents({ startTime, endTime }),
    Events.getRedeemEvents({ startTime, endTime }),
    Events.getFlashStakeEvents({ startTime, endTime }),
    fetchHistoricalPrices({
      maxTime: endTime,
      minTime: startTime,
      coinPairs: ["SUI/USD"],
    }),
  ]);
  const dailyRevenueMap: { [time: number]: Decimal } = {};
  for (const event of epochChangeEvents) {
    const time = getEndOfDayGMT(Number(event.timestamp));
    const timePricePair = suiHistoricalPrices["SUI/USD"].find(
      (o) => o[0] === time,
    );
    if (!timePricePair) {
      console.warn(
        `Sui price not found for ${event.timestamp} from DB. Skipping this entry`,
      );
      continue;
    }
    const fees = new Decimal(event.event.spread_fee)
      .div(1e9)
      .mul(new Decimal(timePricePair[1]));
    if (!dailyRevenueMap[time]) dailyRevenueMap[time] = new Decimal(0);
    dailyRevenueMap[time] = dailyRevenueMap[time].add(fees);
  }
  for (const event of [...mintEvents, ...redeemEvents, ...flashStakeEvents]) {
    const time = getEndOfDayGMT(Number(event.timestamp));
    const timePricePair = suiHistoricalPrices["SUI/USD"].find(
      (o) => o[0] === time,
    );
    if (!timePricePair) {
      console.warn(
        `Sui price not found for ${event.timestamp} from DB. Skipping this entry`,
      );
      continue;
    }
    const fees = new Decimal(event.event.fee_amount)
      .div(1e9)
      .mul(new Decimal(timePricePair[1]));
    if (!dailyRevenueMap[time]) dailyRevenueMap[time] = new Decimal(0);
    dailyRevenueMap[time] = dailyRevenueMap[time].add(fees);
  }
  response = Object.values(dailyRevenueMap)
    .reduce((totalRevenue: Decimal, thisRevenue) => {
      return totalRevenue.add(thisRevenue);
    }, new Decimal(0))
    .div(new Decimal(suiHistoricalPrices["SUI/USD"].length));
  return response.toString();
}
*/
//# sourceMappingURL=protocol.js.map